import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import CompanyClosureChip from './CompanyClosureChip';
import CompanyClosureArchive from './CompanyClosureArchive';
import useCompanyClosureArchive from '../hooks/useCompanyClosureArchive';
import { formatClosureDate } from '../../../../components/company-closure/companyClosure';

const NOTICE_LABELS = [
  { key: 'started', label: 'Início do encerramento' },
  { key: 'd30', label: 'Aviso de 30 dias' },
  { key: 'd10', label: 'Aviso de 10 dias' },
  { key: 'expired', label: 'Fim da carência' },
  { key: 'admin', label: 'Aviso à equipe' },
];

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', textAlign: 'right', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}

function closureRows(closure) {
  const days = Number(closure.days_remaining);
  const requestedBy = closure.requested_by_email ? ` por ${closure.requested_by_email}` : '';
  const rows = [
    { label: 'Situação', value: closure.state === 'expired' ? 'Carência vencida' : 'Em carência' },
    { label: 'Origem', value: closure.origin_label || closure.origin || '—' },
    { label: 'Iniciado em', value: `${formatClosureDate(closure.requested_at) || '—'}${requestedBy}` },
    {
      label: closure.state === 'expired' ? 'Carência terminou em' : 'Fim da carência',
      value: formatClosureDate(closure.scheduled_at) || '—',
    },
  ];
  if (closure.state === 'grace' && Number.isFinite(days)) {
    rows.push({ label: 'Dias restantes', value: Math.max(0, days) });
  }
  if (closure.reason) {
    rows.push({ label: 'Motivo', value: closure.reason });
  }
  return rows;
}

/**
 * CompanyClosureCard - Card "Encerramento" da página da empresa (Admin Geral)
 *
 * Sem ciclo: "Iniciar encerramento" (desabilitado com o motivo quando o
 * servidor não permite). Com ciclo: estado, origem, datas, e-mails enviados e
 * o arquivo de segurança.
 *
 * @param {Object} props
 * @param {Object} props.company - objeto `company` de admin/companies/get.php
 * @param {Function} props.onStart - abre a confirmação de "Iniciar encerramento"
 * @param {Function} [props.onArchiveReady] - recarrega a empresa quando o arquivo fica pronto
 */
function CompanyClosureCard({ company, onStart, onArchiveReady }) {
  const {
    closure,
    running,
    creating,
    downloadingPart,
    error,
    createArchive,
    downloadPart,
  } = useCompanyClosureArchive(company.id, company.closure, onArchiveReady);

  const sentNotices = closure
    ? NOTICE_LABELS.filter((notice) => closure.notices?.[notice.key])
    : [];

  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', color: '#5A80FB' }}>
          <EventBusyOutlinedIcon fontSize="small" />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
          Encerramento
        </Typography>
        {closure && <CompanyClosureChip state={closure.state} scheduledAt={closure.scheduled_at} />}
      </Box>

      {!closure ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Sem encerramento em andamento.
          </Typography>
          <Tooltip title={company.closure_can_start ? '' : (company.closure_start_blocked_message || '')}>
            <span>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<EventBusyOutlinedIcon />}
                onClick={onStart}
                disabled={!company.closure_can_start}
              >
                Iniciar encerramento
              </Button>
            </span>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {closureRows(closure).map((row) => (
              <InfoRow key={row.label} label={row.label} value={row.value} />
            ))}

            <Divider sx={{ my: 1 }} />

            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              E-mails enviados
            </Typography>
            {sentNotices.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Nenhum e-mail enviado ainda.</Typography>
            ) : (
              sentNotices.map((notice) => (
                <InfoRow
                  key={notice.key}
                  label={notice.label}
                  value={formatClosureDate(closure.notices[notice.key])}
                />
              ))
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <CompanyClosureArchive
              closure={closure}
              running={running}
              creating={creating}
              downloadingPart={downloadingPart}
              onCreate={createArchive}
              onDownload={downloadPart}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </Box>
      )}
    </Card>
  );
}

export default CompanyClosureCard;
